import { useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Spin, Tooltip } from "antd"
import type { AxiosResponse } from "axios"
import { CollapseElevations } from "components/ui/collapse/CollapseElevations/CollapseElevations"
import { PageIcon } from "components/ui/customWebviewer/ElevationReferencesSidebar/icons/PageIcon"
import { defaultTooltipStyles, getShadowTooltipContainer } from "components/ui/customWebviewer/shared/shadowTooltip"
import { ArrowUpRightIcon } from "components/ui/icons/arrowUpRight/ArrowUpRightIcon"
import { useAutoConnectErefEcalls } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { applyAutoConnectErefIntoCache } from "helpers/quotationCache.helper"
import AnnotationService from "services/annotations.service"
import type { TEcall, TErefWithFurniture } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import style from "./ElevationCallouts.module.scss"

const {
	elevationCallouts,
	label,
	header,
	action,
	actionDisabled,
	connecting,
	calloutList,
	calloutItem,
	calloutDot,
	calloutText,
	newBadge,
	collapseLabel,
	viewButton,
	viewButtonLabel,
} = style

type Props = {
	eref: TErefWithFurniture
	ecalls: TEcall[]
	onEcallClick?: (ecall: TEcall) => void
}

type TCalloutEntry = {
	key: string
	elevationId: number
	ecall: TEcall
	isNew: boolean
}

export const ElevationCallouts = ({ eref, ecalls, onEcallClick }: Props) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutate: autoConnect, isPending } = useAutoConnectErefEcalls()
	const [newElevationIds, setNewElevationIds] = useState<Set<number>>(new Set())
	const [openPages, setOpenPages] = useState<Set<string>>(new Set())

	const groupedByPage = useMemo(() => {
		const groups = new Map<string, TCalloutEntry[]>()
		ecalls.forEach((ecall) => {
			ecall.elevation_numbers.forEach((en) => {
				if (en.eref_id !== eref.id) return
				const pageKey = ecall.location_page_name ?? "Unknown"
				const entry: TCalloutEntry = {
					key: `${ecall.id}-${en.id}`,
					elevationId: en.id,
					ecall,
					isNew: newElevationIds.has(en.id),
				}
				const list = groups.get(pageKey)
				if (list) list.push(entry)
				else groups.set(pageKey, [entry])
			})
		})
		return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
	}, [ecalls, eref.id, newElevationIds])

	const isDisabled = !eref.elevation_number
	const tooltipText = isDisabled
		? "Set an elevation number on this reference to enable auto-connect"
		: "Find and connect matching callouts to this elevation reference"

	const handleFindAndConnect = () => {
		if (!quotationId || isPending || isDisabled) return
		autoConnect(
			{ quotationId, erefId: eref.id, body: { eref_version: eref.version } },
			{
				onSuccess: (response) => {
					const data = response?.data
					if (!data) return
					const cached = queryClient.getQueryData<AxiosResponse<TQuotationDetail> | undefined>([
						"quotation",
						quotationId,
					])
					const { next, addedElevationIds } = applyAutoConnectErefIntoCache(cached, data)
					queryClient.setQueryData(["quotation", quotationId], next)

					// Repaint the newly-connected callouts: each now links to this eref and
					// turns yellow if the eref has furniture. Cache changes alone don't repaint.
					const connectedEcallIds = new Set(data.connected_ecalls.map((c) => c.id))
					for (const page of next?.data?.related_objects ?? []) {
						for (const callout of page.ecalls) {
							if (connectedEcallIds.has(callout.id)) AnnotationService.syncEcallConnectivity(callout)
						}
					}
					if (addedElevationIds.length > 0) {
						setNewElevationIds((prev) => {
							const merged = new Set(prev)
							addedElevationIds.forEach((id) => merged.add(id))
							return merged
						})
						const addedIdSet = new Set(addedElevationIds)
						const newlyAddedPages = new Set<string>()
						for (const ecall of next?.data?.related_objects?.flatMap((p) => p.ecalls) ?? []) {
							for (const en of ecall.elevation_numbers) {
								if (en.eref_id === eref.id && addedIdSet.has(en.id)) {
									newlyAddedPages.add(ecall.location_page_name ?? "Unknown")
								}
							}
						}
						if (newlyAddedPages.size > 0) {
							setOpenPages((prev) => {
								const merged = new Set(prev)
								newlyAddedPages.forEach((p) => merged.add(p))
								return merged
							})
						}
					}
				},
				onError: formatError,
			},
		)
	}

	return (
		<div className={elevationCallouts}>
			<div className={header}>
				<span className={label}>Elevation callouts</span>
				{isPending ? (
					<span className={connecting}>
						Connecting
						<Spin size="small" />
					</span>
				) : (
					<Tooltip
						title={tooltipText}
						placement="top"
						getPopupContainer={getShadowTooltipContainer}
						styles={defaultTooltipStyles}
					>
						<button
							type="button"
							className={`${action} ${isDisabled ? actionDisabled : ""}`}
							onClick={handleFindAndConnect}
							disabled={isDisabled}
						>
							Find and connect
						</button>
					</Tooltip>
				)}
			</div>
			{groupedByPage.map(([pageName, entries]) => (
				<CollapseElevations
					key={pageName}
					activeKey={openPages.has(pageName) ? [pageName] : []}
					onChange={(keys) => {
						const isOpen = Array.isArray(keys) ? keys.includes(pageName) : keys === pageName
						setOpenPages((prev) => {
							const next = new Set(prev)
							if (isOpen) next.add(pageName)
							else next.delete(pageName)
							return next
						})
					}}
					items={[
						{
							label: (
								<div className={collapseLabel}>
									<span>
										<PageIcon />
										Page
									</span>
									<span>{pageName}</span>
								</div>
							),
							key: pageName,
							children: (
								<div className={calloutList}>
									{entries.map((entry) => (
										<div
											key={entry.key}
											className={calloutItem}
											onClick={() => onEcallClick?.(entry.ecall)}
										>
											<div className={calloutDot} />
											<span className={calloutText}>
												Callout{entry.ecall.room_name ? ` ${entry.ecall.room_name}` : ""}
											</span>
											{entry.isNew && <span className={newBadge}>New</span>}
											<button
												type="button"
												className={viewButton}
												onClick={(e) => {
													e.stopPropagation()
													onEcallClick?.(entry.ecall)
												}}
											>
												<span className={viewButtonLabel}>View</span>
												<ArrowUpRightIcon />
											</button>
										</div>
									))}
								</div>
							),
						},
					]}
				/>
			))}
		</div>
	)
}
